import jwtDecode from "jwt-decode";
import { getSOADetails, getPropertyById, getUserDetails, getSOADetailsByToken } from "../../../api";
import * as Yup from 'yup';

export const defaultSOA = {
  tenant: [
    {
      name: '',
      email: ''
    }
  ],
  address: {
    street: '',
    city: '',
    province: '',
    postal_code: '',
    country: ''
  },
  rental_period: {
    start_date: '',
    end_date: ''
  },
  property_details: {
    property_id: '',
    building_name: '',
    unit_no: ''
  },
  account_details: {
    security: 0,
    interest: 0,
    early_move_out_prorate: 0,
    deductions: []
  },
  statement_details: {
    mailed_on: '',
    address: ''
  },
  landlord_details: {
    name: '',
    address: '',
    phone: '',
    manager_id: '',
    signature: ''
  },
  email_details: {
    name: '',
    email: '',
    cc: [],
    attachments: [],
    notes: ''
  },
  approvals: [
    {
      email: '',
      status: 'pending',
      signature: '',
      comments: '',
      updated_at: null
    }
  ],
  auditor_approval: {
    email: '',
    status: 'pending',
    signature: '',
    comments: '',
    updated_at: null,
    company_id: ''
  }
};

/**
 * Fetch property and manager details for a given property ID
 * @param {string} propertyId - Property ID
 * @returns {Promise<Object>} Property and manager details
 */
export const getPropertyDetailsWithManager = async (propertyId) => {
  try {
    if (!propertyId) {
      throw new Error('Property ID is required');
    }

    const token = localStorage.getItem('accessToken');
    const user = token ? jwtDecode(token) : null;

    if (!user || !user.id || !user.role) {
      throw new Error('User details are missing from token');
    }

    const property = await getPropertyById(propertyId);
    if (!property) {
      throw new Error('Property not found');
    }

    let managerDetails = null;
    if (user.id && user.role) {
      managerDetails = await getUserDetails(user.id, user.role);
    }

    return {
      property,
      manager: managerDetails,
      currentUser: {
        id: user.id,
        role: user.role,
      },
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to construct complete address from property details and unit number
 * @param {Object} addressComponents - Address components from property
 * @param {string} unitNo - Unit number
 * @returns {string} Constructed complete address
 */
const constructCompleteAddress = (addressComponents, unitNo) => {
  const parts = [];

  // Ensure unitNo is a string or empty
  if (unitNo && typeof unitNo === 'string') {
    parts.push(`Unit ${unitNo.trim()}`);
  }

  // Ensure addressComponents fields are strings
  if (addressComponents.street && typeof addressComponents.street === 'string') {
    parts.push(addressComponents.street.trim());
  }

  const locationParts = [];
  if (addressComponents.city && typeof addressComponents.city === 'string') {
    locationParts.push(addressComponents.city.trim());
  }
  if (addressComponents.province && typeof addressComponents.province === 'string') {
    locationParts.push(addressComponents.province.trim());
  }
  if (addressComponents.postal_code && typeof addressComponents.postal_code === 'string') {
    locationParts.push(addressComponents.postal_code.trim());
  }

  if (locationParts.length > 0) {
    parts.push(locationParts.join(', '));
  }

  if (addressComponents.country && typeof addressComponents.country === 'string') {
    parts.push(addressComponents.country.trim());
  }

  return parts.join(', ') || '';
};

/**
 * Auto-fill property and manager details in SOA form
 * @param {Object} currentFormValues - Current form values
 * @param {string} propertyId - Selected property ID
 * @param {string} buildingName - Selected building name
 * @param {string} unitNo - Selected unit number
 * @param {string} selectedBuildingId - Optional: Selected building ID for direct matching
 * @returns {Promise<Object>} Updated form values with auto-filled data
 */
export const autoFillPropertyDetails = async (
  currentFormValues,
  propertyId,
  buildingName = '',
  unitNo = '',
  selectedBuildingId = null
) => {
  try {
    if (!propertyId) {
      return currentFormValues;
    }

    const { property, manager, currentUser } = await getPropertyDetailsWithManager(propertyId);
    if (!property) throw new Error('Property data not found');

    // Find the selected building from property.building array
    let selectedBuilding = null;
    if (property.building && Array.isArray(property.building)) {
      if (selectedBuildingId) {
        // Match by building ID if provided
        selectedBuilding = property.building.find(b => b._id?.toString() === selectedBuildingId);
      } else if (buildingName) {
        // Match by building name
        selectedBuilding = property.building.find(b => b.buildingName === buildingName);
      }
      
      // Fallback to first building if no match found but buildings exist
      if (!selectedBuilding && property.building.length > 0) {
        selectedBuilding = property.building[0];
      }
    }

    const streetFromBuilding =
      currentFormValues.address?.street ||
      buildingName ||
      property.building?.[0]?.buildingName ||
      (typeof property.address === 'string' ? property.address : '') ||
      '';

    const postalCode = 
      currentFormValues.address?.postal_code || 
      (selectedBuilding?.postalCode && typeof selectedBuilding.postalCode === 'string' ? selectedBuilding.postalCode : '') ||
      (typeof property.postal_code === 'string' ? property.postal_code : '') || 
      '';

    const addressComponents = {
      street: streetFromBuilding,
      city: currentFormValues.address?.city || (typeof property.city === 'string' ? property.city : '') || '',
      province: currentFormValues.address?.province || (typeof property.province === 'string' ? property.province : '') || '',
      postal_code: postalCode,
      country: currentFormValues.address?.country || (typeof property.country === 'string' ? property.country : 'Canada') || 'Canada',
    };

    let landlordAddress = currentFormValues.landlord_details?.address || '';
    if (manager?.address && typeof manager.address === 'object') {
      landlordAddress = constructCompleteAddress(manager.address, '');
    }

    const updatedValues = {
      ...currentFormValues,
      tenant: currentFormValues.tenant || [{ name: '', email: '' }],
      address: addressComponents,
      rental_period: {
        start_date: currentFormValues.rental_period?.start_date || '',
        end_date: currentFormValues.rental_period?.end_date || '',
      },
      property_details: {
        property_id: currentFormValues.property_details?.property_id || propertyId,
        building_name:
          currentFormValues.property_details?.building_name ||
          (typeof buildingName === 'string' ? buildingName : '') ||
          property.building?.[0]?.buildingName ||
          (typeof property.title === 'string' ? property.title : '') ||
          '',
        unit_no: currentFormValues.property_details?.unit_no || (typeof unitNo === 'string' ? unitNo : '') || '',
      },
      account_details: {
        security: currentFormValues.account_details?.security || 0,
        interest: currentFormValues.account_details?.interest || 0,
        early_move_out_prorate: currentFormValues.account_details?.early_move_out_prorate || 0,
        deductions: currentFormValues.account_details?.deductions || [],
      },
      statement_details: {
        mailed_on: currentFormValues.statement_details?.mailed_on || new Date().toISOString().split('T')[0],
        address: currentFormValues.statement_details?.address || '',
      },
      landlord_details: {
        name: currentFormValues.landlord_details?.name || '',
        address: landlordAddress,
        phone: currentFormValues.landlord_details?.phone || '',
        manager_id: currentFormValues.landlord_details?.manager_id || currentUser?.id || '',
        signature: currentFormValues.landlord_details?.signature || '',
      },
      email_details: currentFormValues.email_details || {
        name: '',
        email: '',
        cc: [],
        attachments: [],
        notes: '',
      },
      approvals: currentFormValues.approvals || [
        {
          email: '',
          status: 'pending',
          signature: '',
          comments: '',
          updated_at: null,
        },
      ],
      auditor_approval: currentFormValues.auditor_approval || {
        email: '',
        status: 'pending',
        signature: '',
        comments: '',
        updated_at: null,
        company_id: (typeof manager?.company_id === 'string' ? manager.company_id : '') || (typeof property.company_id === 'string' ? property.company_id : '') || '',
      },
    };

    if (!updatedValues.landlord_details.name) {
      updatedValues.landlord_details = {
        ...updatedValues.landlord_details,
        name:
          currentFormValues.landlord_details?.name ||
          (manager
            ? (typeof manager.name === 'string' ? manager.name : '') ||
            (typeof manager.company_name === 'string' ? manager.company_name : '')
            : (typeof property.company === 'string' ? property.company : '') ||
            (typeof property.landlord === 'string' ? property.landlord : '') ||
            (typeof property.owner === 'string' ? property.owner : '')) ||
          '',
        phone:
          currentFormValues.landlord_details?.phone ||
          (manager
            ? (typeof manager.phone === 'string' ? manager.phone : '') ||
            (typeof manager.contact_number === 'string' ? manager.contact_number : '')
            : '') ||
          '',
        signature:
          currentFormValues.landlord_details?.signature ||
          (manager && typeof manager.signature === 'string' ? manager.signature : '') ||
          '',
        manager_id: currentFormValues.landlord_details?.manager_id || currentUser?.id || '',
      };
    }

    return updatedValues;
  } catch (error) {
    console.error('Failed to auto-fill property details:', error.message);
    return currentFormValues;
  }
};

/**
 * Get SOA form initial values, either from fetched data or default values
 * @param {string} [id] - Optional SOA ID to fetch details
 * @param {string} [token] - Optional approval token for fetching details
 * @returns {Promise<Object>} Formik initial values for SOA form
 */
export const getSOAInitialValues = async (id, token) => {
  if (!id) {
    return defaultSOA;
  }

  try {
    const soaDetails = token
      ? (await getSOADetailsByToken(id, token))
      : (await getSOADetails(id)).data;

    return {
      tenant: soaDetails.tenant?.map(t => ({
        name: t.name || '',
        email: !t.email || t.email.toLowerCase() === 'n/a' ? 'N/A' : t.email,
      })) || [{ name: '', email: '' }],

      address: {
        street: soaDetails.address?.street || '',
        city: soaDetails.address?.city || '',
        province: soaDetails.address?.province || '',
        postal_code: soaDetails.address?.postal_code || '',
        country: soaDetails.address?.country || '',
      },

      rental_period: {
        start_date: soaDetails.rental_period?.start_date
          ? new Date(soaDetails.rental_period.start_date).toISOString().split('T')[0]
          : '',
        end_date: soaDetails.rental_period?.end_date
          ? new Date(soaDetails.rental_period.end_date).toISOString().split('T')[0]
          : '',
      },

      property_details: {
        property_id: soaDetails.property_details?.property_id?._id || soaDetails.property_details?.property_id || '',
        building_name: soaDetails.property_details?.building_name || '',
        unit_no: soaDetails.property_details?.unit_no || '',
      },

      account_details: {
        security: soaDetails.account_details?.security || 0,
        interest: soaDetails.account_details?.interest || 0,
        early_move_out_prorate: soaDetails.account_details?.early_move_out_prorate || 0,
        deductions: soaDetails.account_details?.deductions || [],
      },

      statement_details: {
        mailed_on: soaDetails.statement_details?.mailed_on
          ? new Date(soaDetails.statement_details.mailed_on).toISOString().split('T')[0]
          : '',
        address: soaDetails.statement_details?.address || '',
      },

      landlord_details: {
        name: soaDetails.landlord_details?.name || '',
        address: soaDetails.landlord_details?.address || '',
        phone: soaDetails.landlord_details?.phone || '',
        manager_id: soaDetails.landlord_details?.manager_id || '',
        signature: soaDetails.landlord_details?.signature || '',
      },

      email_details: soaDetails.email_details || {
        name: '',
        email: '',
        cc: [],
        attachments: [],
        notes: ''
      },

      approvals: soaDetails.approvals || [
        {
          email: '',
          status: 'pending',
          signature: '',
          comments: '',
          updated_at: null
        }
      ],

      auditor_approval: {
        email: soaDetails.auditor_approval?.email || '',
        status: soaDetails.auditor_approval?.status || 'pending',
        signature: soaDetails.auditor_approval?.signature || '',
        comments: soaDetails.auditor_approval?.comments || '',
        updated_at: soaDetails.auditor_approval?.updated_at || null,
        company_id: soaDetails.auditor_approval?.company_id || ''
      },
    };
  } catch (error) {
    return defaultSOA;
  }
};